import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import attViewerPrizmView from './attViewerPrizmComponent.html';
import './attViewerPrizmComponent.css';
import stemmer from 'stemmer';
import _ from 'lodash';

export function attViewerPrizmComponent() {
	return {
		bindings: {
			attachment: '<',
			searchTerm: '<?',
		},
		controllerAs: 'vm',
		controller: attViewerPrizmComponentCtrl,
		template: attViewerPrizmView,
	};
}

let bundleData;
let bundlePromise;

function loadPrizmBundle() {
	if (!bundlePromise) {
		bundlePromise = Promise.all([
			// HATE having to put this in there, but without it, webpack tries to resolve it rather
			// than letting the browser do its thing.
			import(/*webpackIgnore: true*/ `${window.BASE_URL}/angular/assets/js/PrizmDoc/js/index.js`),
			AssetManager.css('/angular/assets/js/PrizmDoc/css/bundle.css'),
		]).then(async ([bundleDataImports]) => {
			bundleData = bundleDataImports;
		});
	}
	return bundlePromise;
}

// begin loading shortly after page load.
// if user clicks a preview link before 5000ms,
// $onChanges will call loadPrizmBundle immediately
// and this second call via setTimeout will do nothing
setTimeout(loadPrizmBundle, 5000);

export class attViewerPrizmComponentCtrl {
	constructor(Api, $element, modal, UrlData) {
		'ngInject';
		this.Api = Api;
		this.$element = $element;
		this.modal = modal;
		this.UrlData = UrlData;
		this.translations = [];
	}

	$onChanges(changes) {
		if (changes.attachment) {
			// make sure our bundles have loaded
			loadPrizmBundle().then(() => {
				if (this.viewerControl) {
					try {
						this.viewerControl.destroy();
						// eslint-disable-next-line no-empty
					} catch (e) {}
				}
				this.initViewer();
			});
		}
	}

	$onDestroy() {
		if (this.viewerControl) {
			try {
				this.viewerControl.destroy();
				// eslint-disable-next-line no-empty
			} catch (e) {}
		}
		this.isDestroyed = true;
	}

	initViewer() {
		this.loadTranslations();
		// set a cookie that we can use to cache page renderings
		window.PCCViewer.Ajax.setHeaders({ 'Prizm-Attachment-Id': this.attachment.id });
		// start a session for the viewer
		return this.Api.get(`/files/${this.attachment.id}/viewer`, {
			svg_format: this.getSvgFormat(),
		}).then(this.startViewingSession.bind(this));
	}

	async startViewingSession(session) {
		if (!session || !session.viewingSessionId) {
			this.modal.alert({ text: 'Error previewing document. Please try again later.' });
			return;
		}
		// see http://help.accusoft.com/PrizmDoc/v12.3/HTML/external-jQuery.fn.html#~Options
		const pluginOptions = {
			documentID: session.viewingSessionId,
			imageHandlerUrl: session.baseUrl,
			language: bundleData.customizations.languages,
			template: bundleData.customizations.template,
			uiElements: {
				annotateTab: false,
				redactTab: false,
				esignTab: false,
			},
			predefinedSearch: {},
		};
		if (this.searchTerm && typeof this.searchTerm == 'string' && !this.attachment.is_translated) {
			// load the doc with search terms highlighted
			// see http://help.accusoft.com/PrizmDoc/v13.2/HTML/webframe.html#How_to_use_Predefined_Search.html
			pluginOptions.predefinedSearch.terms = this.getEquivalentTerms().map(searchTerm => {
				const hasWhitespace = /\s/.test(searchTerm);
				return {
					searchTerm,
					selected: true,
					highlightColor: '#1c86ee',
					options: {
						exactPhrase: hasWhitespace,
						matchWholeWord: !hasWhitespace,
					},
				};
			});
		} else if (this.searchTerm && !this.attachment.is_translated) {
			pluginOptions.predefinedSearch.terms = this.formatSearchObjects(this.searchTerm);
		}

		// hide the tab if the user didn't search for anything
		pluginOptions.uiElements.searchTermsTab = pluginOptions.predefinedSearch.terms ? true : false;
		pluginOptions.predefinedSearch.searchOnInit = pluginOptions.predefinedSearch.terms ? true : false;

		//Hide the the search tab on translated posts
		if (this.attachment.is_translated) {
			pluginOptions.uiElements.searchTab = false;
		}
		// If users click between attachments quickly, the .prizm-doc-viewer element won't exist yet
		// So give the DOM a moment to catch up
		const jqViewer = window.$(this.$element[0]).find('.prizm-doc-viewer');
		if (jqViewer.length !== 1) {
			setTimeout(() => {
				if (!this.isDestroyed) {
					this.startViewingSession(session);
				}
			}, 100);
			return;
		}
		// `viewerPlugin` docs:
		// http://help.accusoft.com/PrizmDoc/v12.3/HTML/PCCViewer.Viewer.html
		this.viewerPlugin = jqViewer.pccViewer(pluginOptions);
		this.viewerControl = this.viewerPlugin.viewerControl;
		this.viewerControl.on('ViewerReady', () => {
			this.setViewerOptions();
			if (pluginOptions.predefinedSearch.terms) {
				this.setupSearchTermsTab(pluginOptions);
			}
		});
		this.viewerControl.on('PageDisplayed', e => {
			if (this.translations.length > 0) {
				this.appendTranslation(e.pageNumber);
			}
		});
		this.viewerControl.on('PageDisplayed', e => {
			if (this.translations.length > 0) {
				this.appendTranslation(e.pageNumber);
			}
		});
		this.viewerControl.on('ScaleChanged', e => {
			//zoom font up or down based on scale factor when the user zooms if we have translation
			if (this.attachment.is_translated) {
				let fontSize;
				if (e.trigger === 'Zoom') {
					const scaleFactor = e.scaleFactor;
					switch (true) {
						case scaleFactor <= 0.5:
							fontSize = 11;
							break;
						case scaleFactor <= 0.7:
							fontSize = 12;
							break;
						case scaleFactor <= 0.9:
							fontSize = 14;
							break;
						case scaleFactor <= 1.1:
							fontSize = 16;
							break;
						case scaleFactor <= 1.3:
							fontSize = 18;
							break;
						default:
							fontSize = false;
					}
					if (fontSize) {
						const pages = window.$('.prizm-page-translation');
						pages.each((index, page) => {
							window.$(page).css('font-size', `${fontSize}px`);
						});
					}
				}
			}
		});

		await bundleData.finishImports();
	}
	loadTranslations() {
		if (this.attachment.is_translated) {
			this.Api.get(`/v2/attachments/${this.attachment.id}/pages`).then(data => {
				this.translations = data;
			});
		}
	}
	appendTranslation(pageNumber) {
		const pages = window.$(this.$element[0]).find('.pccPageListItem');
		pages.each((index, value) => {
			const id = window.$(value).attr('id');
			const regex = /[0-9]+(?=__pViewId)/g;
			const prizmViewId = parseInt(id.match(regex)[0], 10);
			//Need pageNumber - 1 because page number 1 corresponds to the prizm id of 0
			if (prizmViewId === pageNumber - 1) {
				if (!window.$(value).find('.prizm-page-translation').length) {
					window
						.$(value)
						.prepend(
							`<div class="prizm-page-translation" ><h1>Translation</h1><p>${_.escape(
								this.translations[pageNumber - 1].content
							)}</p></div>`
						);
				}
				return false;
			}
		});
	}

	/**
	 * Add the search terms to custom Search Term tab in PrizmDoc and handle clicks on the "Match Exact Phrase checkbox"
	 * (the custom tab is added to PrizmDoc viewer in angular/assets/js/PrizmDoc/js/bundle.js)
	 * @param originalPluginOptions Object  the values originally sent to PrizmDoc during the initial creation
	 */
	setupSearchTermsTab(originalPluginOptions) {
		const jqViewer = window.$(this.$element[0]).find('.prizm-doc-viewer');
		let matchExactPhrase = false;
		const searchTermParts = this.searchTerm;

		let userSearchTerm;
		if (this.searchTerm && typeof this.searchTerm == 'string') {
			userSearchTerm = this.searchTerm;
		} else if (this.searchTerm) {
			userSearchTerm = this.searchTerm.map(term => term.word).join(' ');
		}
		const exactMatchTermsToRender = [userSearchTerm];

		const deepSearchTermsToRender = searchTermParts.map(term => term.word);

		/**
		 * Render the search terms into the #PredefinedSearchTermsArea element in PrizmDoc Viewer
		 */
		const render = function (termsToRender) {
			const searchTermPills = termsToRender
				.map(term => {
					return `<span class="search-terms-term">${term}</span>`;
				})
				.join('');
			const formGroupClass = matchExactPhrase ? 'form-group checked' : 'form-group';
			// hides checkbox if there's only one term (and we aren't already in Match Exact Phrase)
			let exactMatchCheckbox = ``;
			if (matchExactPhrase || termsToRender.length > 1) {
				exactMatchCheckbox = `
					<div class="${formGroupClass}" id="CheckboxGroup" onclick="window.toggleMatchExactPhrase(${!matchExactPhrase})">
						<span class="replacement-checkbox"></span>
						<label>Match Exact Phrase</label>
					</div>
				`;
			}
			window.$('#PredefinedSearchTermsArea').html(searchTermPills + exactMatchCheckbox);
		}.bind(this);

		/**
		 * Changes the checked value and instantiates a new doc viewer based on the value of matchExactPhrase (either runs
		 * an exact match search if `matchExactPhrase` or the original search)
		 * @param newVal
		 */
		const toggleMatchExactPhrase = function (newVal) {
			matchExactPhrase = newVal;
			let viewerControl;
			let termsToRender;

			if (matchExactPhrase) {
				let exactMatchOptions = _.cloneDeep(originalPluginOptions);
				// searchOnInit should be true already, but it's not and I don't know why
				exactMatchOptions.predefinedSearch.searchOnInit = true;
				exactMatchOptions.predefinedSearch.terms = [
					{
						searchTerm: userSearchTerm,
						selected: true,
						options: {
							exactPhrase: true,
							matchWholeWord: true,
						},
					},
				];
				const viewer = jqViewer.pccViewer(exactMatchOptions);
				viewerControl = viewer.viewerControl;
				termsToRender = exactMatchTermsToRender;
			} else {
				let notExactMatchOptions = _.cloneDeep(originalPluginOptions);
				// searchOnInit should be true already, but it's not and I don't know why
				notExactMatchOptions.predefinedSearch.searchOnInit = true;
				const viewer = jqViewer.pccViewer(notExactMatchOptions);
				viewerControl = viewer.viewerControl;
				termsToRender = deepSearchTermsToRender;
			}
			viewerControl.on('ViewerReady', () => {
				render(termsToRender);
				// this is the same behavior as `this.setViewerScale()`
				viewerControl.fitContent('FullPage');
			});
		};
		window.toggleMatchExactPhrase = toggleMatchExactPhrase;
		const termsToRender = searchTermParts.map(term => term.word);
		render(termsToRender);
	}

	getEquivalentTerms() {
		const terms = [];
		this.searchTerm
			.toLocaleLowerCase()
			.split('&&')
			.forEach(term => {
				const termFinalLetter = term.slice(-1);
				// naive treatment of possible term and suffixes
				terms.push(term);
				terms.push(term + (termFinalLetter == 's' ? 'es' : 's'));
				terms.push(term + (termFinalLetter == 'e' ? 'd' : 'ed'));
				terms.push(term + 'ing');
				// naive reverse stemming
				const stem = stemmer(term);
				const stemFinalLetter = stem.slice(-1);
				terms.push(stem);
				terms.push(stem + (stemFinalLetter == 's' ? 'es' : 's'));
				terms.push(stem + (stemFinalLetter == 'e' ? 'd' : 'ed'));
				terms.push(stem + 'ing');
			});
		return _.unique(terms);
	}

	formatSearchObjects(searchObjects) {
		const terms = [];
		searchObjects.forEach(object => terms.push(...object.variants));
		console.log('deep search terms:', terms);
		return terms;
	}

	setViewerOptions() {
		this.setViewerScale();
	}

	setViewerScale() {
		if (this.attachment.is_translated) {
			const page = window.$(this.$element[0]).find('.pccPageListItem')[0];
			if (page) {
				const aspectRatio = window.$(page).width() / window.$(page).height();
				if (aspectRatio > 1) {
					//Laptop screens are typically below this. My mp pro is 1600
					if (window.$(window).width() < 1700) {
						this.viewerControl.setScaleFactor(0.55);
					} else {
						this.viewerControl.setScaleFactor(0.7);
					}
					return;
				}
			}
		}
		// if not very portrait, scale to fit width
		this.viewerControl.fitContent('FullPage');
	}

	_getIPCCFormat() {
		try {
			return window.IPCC.J.Yj;
			// eslint-disable-next-line no-empty
		} catch (e) {}
		return false;
	}

	_detectFormat() {
		// lifted from angular/assets/js/PrizmDoc/js/viewercontrol.js
		// http://help.accusoft.com/PrizmDoc/v12.4/HTML/embed-the-viewing-client.html
		const J = {};
		J.Qj = (function () {
			var Qj = false;
			var vd = navigator.userAgent,
				aar =
					vd.indexOf('Mozilla/5.0') > -1 &&
					vd.indexOf('Android ') > -1 &&
					vd.indexOf('AppleWebKit') > -1 &&
					!(vd.indexOf('Chrome') > -1),
				afp = new RegExp(/AppleWebKit\/([\d.]+)/),
				aCe = afp.exec(vd),
				aqr = aCe === null ? null : parseFloat(afp.exec(vd)[1]);
			Qj = aar && aqr !== null && aqr < 537;
			return Qj;
		})();
		J.aAY = (function () {
			var anZ = false;
			var vd = navigator.userAgent,
				aar = /Android/i.test(vd),
				axB = /iPhone|iPad|iPod/i.test(vd),
				ayJ = /IEMobile/i.test(vd);
			anZ = aar || axB || ayJ;
			return anZ;
		})();
		J.Yj = (function () {
			var aiG = navigator.userAgent;
			if (document.documentMode === 10 || J.Qj) {
				return 'svg';
			} else if (document.documentMode === 9) {
				return 'svga';
			} else if (
				/Apple Computer/.test(navigator.vendor || '') &&
				/Mac OS X.*?Version\/9\..*?Safari/i.test(aiG) &&
				!J.aAY
			) {
				return 'svga';
			} else if (/Google Inc/.test(navigator.vendor || '') && /Windows.*?Chrome/i.test(aiG)) {
				return 'svga';
			} else {
				return 'svgb';
			}
		})();
		return J.Yj;
	}

	getSvgFormat() {
		return this._getIPCCFormat() || this._detectFormat();
	}
}
